import { useRouter } from "@core/Api/useRouter";
import Path from "@core/FileProvider/Path/Path";
import type { ClientArticleProps } from "@core/SitePresenter/SitePresenter";
import FetchService from "@core-ui/ApiServices/FetchService";
import MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import { useArticlePropsStore } from "@core-ui/stores/ArticlePropsStore/ArticlePropsStore.provider";
import { getEditorStore } from "@core-ui/stores/EditorStore";
import type { ItemLink } from "@ext/navigation/NavigationLinks";
import type { QuizSettings } from "@ext/quiz/models/types";
import { type Dispatch, type SetStateAction, useCallback } from "react";

export interface PropsEditorTarget {
	itemLink: ItemLink;
	setItemLink: Dispatch<SetStateAction<ItemLink>>;
	isCategory: boolean;
	onExpandedChange?: (itemPath: string, expanded: boolean) => void;
}

interface UsePropsEditorSubmitParams extends PropsEditorTarget {
	item: ClientArticleProps;
	isCurrentItem: boolean;
	onSaved: () => void;
}

export interface PropsEditorData {
	title: string;
	description: string;
	fileName: string;
	quiz?: QuizSettings;
	expanded?: boolean;
	searchPhrases?: string[];
	aliases?: string[];
}

export const usePropsEditorSubmit = (params: UsePropsEditorSubmitParams) => {
	const { item, isCurrentItem, itemLink, setItemLink, isCategory, onExpandedChange, onSaved } = params;

	const apiUrlCreator = ApiUrlCreatorService.value;
	const articleLogicPath = useArticlePropsStore((state) => state.data.logicPath);
	const router = useRouter();

	const updateNavigation = useCallback(
		(updatedPathname: string) => {
			if (isCurrentItem) return router.pushPath(updatedPathname, undefined, { keepSticky: true });

			if (new Path(articleLogicPath).isParentOf(new Path(item.logicPath))) {
				return router.pushPath(articleLogicPath.replace(item.logicPath, updatedPathname), undefined, {
					keepSticky: true,
				});
			}

			void refreshPage?.();
		},
		[isCurrentItem, articleLogicPath, item.logicPath, router],
	);

	return useCallback(
		async (data: PropsEditorData) => {
			const existingAliases = new Map<string, string | { path: string; moved?: string }>(
				(item.aliases ?? []).map((a) => [typeof a === "string" ? a : a.path, a]),
			);
			const newProps: ClientArticleProps = {
				...item,
				title: data.title,
				fileName: data.fileName,
				quiz: data.quiz,
				expanded: data.expanded,
				searchPhrases: data.searchPhrases,
				description: data.description,
				aliases: (data.aliases ?? []).map((path) => existingAliases.get(path) ?? path),
			} as ClientArticleProps;

			const response = await FetchService.fetch(
				apiUrlCreator.updateItemProps(),
				JSON.stringify(newProps),
				MimeTypes.json,
			);

			if (!response.ok) return;

			const { pathname } = await response.json();
			void updateNavigation(pathname);

			if (isCategory && (item.expanded ?? true) !== data.expanded) {
				onExpandedChange?.(itemLink.ref.path, data.expanded);
			}

			itemLink.title = data.title;
			setItemLink({ ...itemLink });

			const editor = getEditorStore().editor;
			if (editor && !editor.isDestroyed && isCurrentItem) {
				const header = editor.view.dom.firstChild as HTMLParagraphElement;
				if (header) header.innerText = data.title;
			}

			onSaved();
		},
		[item, isCurrentItem, itemLink, setItemLink, isCategory, onExpandedChange, onSaved, updateNavigation],
	);
};
